import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  useCohort,
  useCohortMembers,
  useCohortModuleAccess,
  useAddCohortMember,
  useRemoveCohortMember,
  useToggleModuleAccess,
} from "@/hooks/useCohorts";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { CohortDiscussionList } from "@/components/discussions/CohortDiscussionList";
import {
  ArrowLeft,
  Users,
  Lock,
  Unlock,
  Plus,
  BookOpen,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

export default function CohortDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: cohort, isLoading: cohortLoading } = useCohort(id!);
  const { data: members, isLoading: membersLoading } = useCohortMembers(
    id!,
    cohort?.curriculum_id || ""
  );
  const { data: courseModules, isLoading: modulesLoading } =
    useCohortModuleAccess(id!, cohort?.curriculum_id || "");

  const addMemberMutation = useAddCohortMember();
  const removeMemberMutation = useRemoveCohortMember();
  const toggleAccessMutation = useToggleModuleAccess();

  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userSearchResults, setUserSearchResults] = useState<
    { id: string; full_name: string }[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [skippedWarning, setSkippedWarning] = useState<string[] | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<{
    user_id: string;
    full_name: string;
  } | null>(null);

  const searchUsers = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }

    const { data } = await supabase
      .from("profiles")
      .select("id, full_name")
      .ilike("full_name", `%${query}%`)
      .limit(10);

    setUserSearchResults(data || []);
  };

  const handleAddMember = async () => {
    if (!selectedUserId || !cohort) return;

    try {
      const result = await addMemberMutation.mutateAsync({
        cohortId: cohort.id,
        userId: selectedUserId,
        curriculumId: cohort.curriculum_id,
        gatingEnforced: cohort.gating_enabled,
      });

      if (result.skippedCourses.length > 0) {
        setSkippedWarning(result.skippedCourses);
      }

      if (result.enrolledCourses.length > 0) {
        toast({
          title: "Member added",
          description: `Enrolled in ${result.enrolledCourses.length} course(s).`,
        });
      } else if (result.skippedCourses.length > 0) {
        toast({
          title: "Member not added",
          description:
            "All courses were skipped due to existing self-paced enrollments.",
          variant: "destructive",
        });
      }

      setAddMemberOpen(false);
      setSelectedUserId("");
      setSearchQuery("");
      setUserSearchResults([]);
    } catch {
      toast({
        title: "Error",
        description: "Failed to add member.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmRemove = async () => {
    if (!memberToRemove || !cohort) return;
    try {
      await removeMemberMutation.mutateAsync({
        cohortId: cohort.id,
        userId: memberToRemove.user_id,
      });
      toast({
        title: "Member removed",
        description: `${memberToRemove.full_name} has been removed from this cohort.`,
      });
      setMemberToRemove(null);
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove member.",
        variant: "destructive",
      });
    }
  };

  const handleToggleModule = async (
    moduleId: string,
    currentlyUnlocked: boolean
  ) => {
    if (!cohort) return;
    try {
      await toggleAccessMutation.mutateAsync({
        cohortId: cohort.id,
        moduleId,
        unlock: !currentlyUnlocked,
      });
      toast({
        title: currentlyUnlocked ? "Module locked" : "Module unlocked",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to update module access.",
        variant: "destructive",
      });
    }
  };

  if (cohortLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48" />
        </div>
      </DashboardLayout>
    );
  }

  if (!cohort) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold">Cohort not found</h2>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/instructor/courses")}
          >
            Back to Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/instructor/courses")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{cohort.name}</h1>
            <p className="text-muted-foreground text-sm">
              {cohort.curriculum_title}
              {cohort.start_date &&
                ` · Started ${format(new Date(cohort.start_date), "MMM d, yyyy")}`}
            </p>
          </div>
          <div className="flex gap-2">
            <Badge variant={cohort.is_active ? "default" : "secondary"}>
              {cohort.is_active ? "Active" : "Inactive"}
            </Badge>
            <Badge variant={cohort.gating_enabled ? "outline" : "secondary"}>
              {cohort.gating_enabled ? (
                <>
                  <Lock className="h-3 w-3 mr-1" /> Gated
                </>
              ) : (
                <>
                  <Unlock className="h-3 w-3 mr-1" /> Open
                </>
              )}
            </Badge>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="members" className="space-y-4">
          <TabsList>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="modules">Module Access</TabsTrigger>
            <TabsTrigger value="discussions">Discussions</TabsTrigger>
          </TabsList>

          <TabsContent value="discussions" className="space-y-4">
            <CohortDiscussionList cohortId={id!} />
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setAddMemberOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Member
              </Button>
            </div>

            {skippedWarning && skippedWarning.length > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  The following courses were skipped because the user already has
                  a self-paced enrollment: {skippedWarning.join(", ")}
                </AlertDescription>
              </Alert>
            )}

            {membersLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            ) : members && members.length > 0 ? (
              <div className="space-y-2">
                {members.map((member) => (
                  <Card key={member.user_id}>
                    <CardContent className="flex items-center gap-4 py-3">
                      <UserAvatar
                        avatarPath={member.avatar_url}
                        fullName={member.full_name}
                        className="h-8 w-8"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{member.full_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {member.enrolled_courses} / {member.total_courses}{" "}
                          courses enrolled
                        </p>
                      </div>
                      <div className="hidden sm:flex flex-col items-end gap-1 w-40">
                        <div className="flex items-center justify-between w-full text-xs">
                          <span className="text-muted-foreground">Modules</span>
                          <span className="font-medium">
                            {member.completed_modules}/{member.total_modules}
                            {" "}({member.progress_percent}%)
                          </span>
                        </div>
                        <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${member.progress_percent}%` }}
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setMemberToRemove({
                            user_id: member.user_id,
                            full_name: member.full_name,
                          })
                        }
                        aria-label={`Remove ${member.full_name}`}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}

              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">No members yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Add members to this cohort
                  </p>
                  <Button onClick={() => setAddMemberOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Member
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Module Access Tab */}
          <TabsContent value="modules" className="space-y-4">
            {!cohort.gating_enabled && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Gating is disabled for this cohort. All modules are accessible
                  to members regardless of the settings below.
                </AlertDescription>
              </Alert>
            )}

            {modulesLoading ? (
              <div className="space-y-4">
                {[...Array(2)].map((_, i) => (
                  <Skeleton key={i} className="h-32" />
                ))}
              </div>
            ) : courseModules && courseModules.length > 0 ? (
              <div className="space-y-6">
                {courseModules.map((courseGroup) => (
                  <div key={courseGroup.course_id}>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      {courseGroup.course_title}
                    </h3>
                    <div className="space-y-2">
                      {courseGroup.modules.length > 0 ? (
                        courseGroup.modules.map((mod) => (
                          <Card key={mod.id}>
                            <CardContent className="flex items-center justify-between py-3">
                              <div>
                                <p className="font-medium text-sm">
                                  {mod.title}
                                </p>
                                {mod.unlocked_at && (
                                  <p className="text-xs text-muted-foreground">
                                    Unlocked{" "}
                                    {format(
                                      new Date(mod.unlocked_at),
                                      "MMM d, yyyy"
                                    )}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Label className="text-xs">
                                  {mod.is_unlocked ? (
                                    <span className="flex items-center gap-1 text-primary">
                                      <Unlock className="h-3 w-3" /> Unlocked
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-muted-foreground">
                                      <Lock className="h-3 w-3" /> Locked
                                    </span>
                                  )}
                                </Label>
                                <Switch
                                  checked={mod.is_unlocked}
                                  onCheckedChange={() =>
                                    handleToggleModule(mod.id, mod.is_unlocked)
                                  }
                                  disabled={toggleAccessMutation.isPending}
                                />
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground pl-6">
                          No modules in this course
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">
                    No courses in curriculum
                  </h3>
                  <p className="text-muted-foreground">
                    Add courses to the curriculum first
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Member to Cohort</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Search by name</Label>
              <Input
                placeholder="Type a name..."
                value={searchQuery}
                onChange={(e) => searchUsers(e.target.value)}
              />
            </div>

            {userSearchResults.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Select a user
                </Label>
                <Select
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose user" />
                  </SelectTrigger>
                  <SelectContent>
                    {userSearchResults.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              onClick={handleAddMember}
              disabled={!selectedUserId || addMemberMutation.isPending}
              className="w-full"
            >
              {addMemberMutation.isPending ? "Adding..." : "Add Member"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!memberToRemove}
        onOpenChange={(open) => !open && setMemberToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {memberToRemove?.full_name} from this cohort? This will
              revoke their access to all cohort modules.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeMemberMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              disabled={removeMemberMutation.isPending}
            >
              {removeMemberMutation.isPending ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
